import React from 'react';
import type { UnifiedProductSummary } from '@noths/polaris-client-unified-product-service';

import { SectionContainer } from 'src/components/molecules/SectionContainer/SectionContainer';
import { PartnerBlock } from 'src/components/organisms/PartnerBlock/PartnerBlock';
import { transformUnifiedProductToPartnerProduct } from 'src/components/organisms/PartnerBlock/transformer';
import type { PartnerProfileIndexes } from 'src/components/organisms/PartnerBlock/types';
import type { PartnerModel } from 'src/content/sections/partnerBlock/types';
import { usePromoteAsSale } from 'src/context/discountPresentationMode/DiscountPresentationModeContext';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import { trackItemsImpression } from 'src/tracking/common/trackItemsImpression';
import { transformUnifiedProductForTracking } from 'src/tracking/common/unifiedProductTransformer';
import { trackLinkClick } from 'src/tracking/components/partnerBlock';
import type { TrackingContext } from 'src/types/TrackingContext';
import * as styles from './PartnerBlockSection.style';

const hideNonPurchasableProducts = (products: UnifiedProductSummary[]) =>
  products.filter((product) => product.purchasable);

export interface PartnerBlockSectionProps {
  copy: string;
  heading: string;
  lazyImages?: boolean;
  pageType: string;
  partners: PartnerModel[];
  trackingPosition?: number;
  trackingTitle: string;
}

export const PartnerBlockSection = ({
  copy,
  heading,
  lazyImages,
  pageType,
  partners,
  trackingPosition,
  trackingTitle,
}: PartnerBlockSectionProps) => {
  const promoteAsSale = usePromoteAsSale();
  const trackingContext: TrackingContext = {
    trackingContentType: 'Partner block',
    trackingPosition,
    trackingTitle,
  };

  const partnersWithRenderedProducts = partners.map((partner) => ({
    ...partner,
    products: hideNonPurchasableProducts(partner.products),
  }));

  const partnersWithTransformedProducts = partnersWithRenderedProducts.map((partner) => ({
    ...partner,
    products: partner.products.map((product) =>
      transformUnifiedProductToPartnerProduct(product, {
        priority: !lazyImages,
        promoteAsSale,
      }),
    ),
  }));

  return (
    <SectionContainer wrapperStyles={styles.wrapper}>
      <PartnerBlock
        copy={copy}
        heading={heading}
        lazyImages={lazyImages}
        onPartnerClick={(
          e: React.MouseEvent<HTMLAnchorElement>,
          { partnerIndex }: { partnerIndex: number },
        ) => {
          trackLinkClick(e, {
            ...trackingContext,
            numberOfPartners: partnersWithRenderedProducts.length,
            partnerIndex,
            // get the partner name from the first product
            // assume both products are from the same partner
            partnerName:
              partnersWithRenderedProducts[partnerIndex].products[0].attributes.partnerName ?? '',
            partnerUrl: partnersWithRenderedProducts[partnerIndex].url,
          });
        }}
        onProductClick={(
          e: React.MouseEvent<HTMLAnchorElement>,
          { partnerIndex, productIndex }: PartnerProfileIndexes,
        ) => {
          if (partnersWithRenderedProducts[partnerIndex].products.length > 0) {
            trackProductClick(e, {
              ...trackingContext,
              pageType,
              numberOfItems: partnersWithRenderedProducts[partnerIndex].products.length,
              product: transformUnifiedProductForTracking(
                partnersWithRenderedProducts[partnerIndex].products[productIndex],
              ),
              productIndex,
            });
          }
        }}
        onProductVisible={({ partnerIndex }: { partnerIndex: number }) => {
          if (partnersWithRenderedProducts[partnerIndex].products.length > 0) {
            trackProductImpression({
              ...trackingContext,
              pageType,
              products: partnersWithRenderedProducts[partnerIndex].products.map(
                transformUnifiedProductForTracking,
              ),
            });
          }
        }}
        onVisible={() =>
          trackItemsImpression({
            ...trackingContext,
            numberOfItems: partnersWithRenderedProducts.length,
          })
        }
        partners={partnersWithTransformedProducts}
      />
    </SectionContainer>
  );
};
